'use client';

import { useEffect } from 'react';

import type { OtpVerificationModalProps } from '@/components/OtpVerificationModal';
import { motion } from '@/libs';
import { cleanupExatoDocCheck } from '@/utils/exato';

import { Button, FormField, InputForm, formatPhone } from '../../../ui';

import { useRecoveryPhoneForm } from '../../hooks';

type RecoveryPhoneFormProps = {
  cpf: string;
  onSuccess: () => void;
  onOtpVerificationChange?: (otpVerification: OtpVerificationModalProps | null) => void;
};

export const RecoveryPhoneForm = ({
  cpf,
  onSuccess,
  onOtpVerificationChange,
}: RecoveryPhoneFormProps) => {
  const { control, errors, isLoading, otpVerification, handleSubmit } =
    useRecoveryPhoneForm({
      cpf,
      onSuccess,
    });

  useEffect(() => {
    cleanupExatoDocCheck();
  }, []);

  useEffect(() => {
    onOtpVerificationChange?.(otpVerification);

    return () => onOtpVerificationChange?.(null);
  }, [onOtpVerificationChange, otpVerification]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="flex flex-col gap-4"
    >
      <div className="text-center">
        <h2 className="text-lg font-semibold text-foreground">Novo telefone</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Digite o telefone celular que você quer usar para acessar a conta.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <FormField error={errors.newPhone} label="Novo telefone">
          <InputForm
            control={control}
            name="newPhone"
            placeholder="(00) 00000-0000"
            formatFunction={formatPhone}
            inputMode="numeric"
            autoComplete="tel"
          />
        </FormField>

        <FormField error={errors.confirmNewPhone} label="Confirmar novo telefone">
          <InputForm
            control={control}
            name="confirmNewPhone"
            placeholder="(00) 00000-0000"
            formatFunction={formatPhone}
            inputMode="numeric"
            autoComplete="tel"
          />
        </FormField>

        <Button
          type="submit"
          loading={isLoading}
          disabled={isLoading}
          loadingText="Atualizando telefone..."
          variant="primary"
          size="large"
          className="h-12 w-full text-base"
        >
          Atualizar telefone
        </Button>
      </form>
    </motion.div>
  );
};
